import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FaTrash, FaTimes } from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';
import { isTwoHandDumbbellExercise, formatWeight, formatReps, formatDuration } from '../../utils/weightFormatter';

const WorkoutList = ({ 
  workouts, 
  readOnly = false,
  onDataChanged, // 데이터 변경 시 부모 컴포넌트에 알림용 콜백
  setWorkouts,
  api // API 객체 직접 전달
}) => {
  // 내부 상태 관리
  const [isProcessing, setIsProcessing] = useState(false); // API 요청 처리 중 상태
  const [editingWorkout, setEditingWorkout] = useState(null); // 워크아웃 이름 편집 상태
  const [editingSet, setEditingSet] = useState(null); // 세트 편집 상태
  
  // 워크아웃 이름 편집 시작
  const handleEditWorkout = (workout) => {
    setEditingWorkout(workout);
    setEditingSet(null);
  };
  
  // 세트 편집 시작
  const handleEditSet = (workout, set) => {
    // 데이터 값을 기반으로 운동 유형 판단
    // 초기 데이터 객체
    const data = {};
    
    // 한 손으로만 하는 덤벨 운동 목록 (단손 운동)
    const oneArmDumbellExercises = [
      '원 암 덤벨 로우',
      '원 암 덤벨 숄더 프레스',
      '원 암 덤벨 컬',
      '싱글 덤벨 로우',
      '싱글 덤벨 프레스',
      '싱글 덤벨 컬'
    ];
    
    // 문자열에서 숫자 추출 및 데이터 값 확인
    const hasDumbell = workout.name.includes('덤벨');
    
    // 양손 덤벨 운동인지 확인 (덤벨이 포함되지만 원 암/싱글 덤벨이 아닌 경우)
    const isBothArmsDumbellExercise = isTwoHandDumbbellExercise(workout.name);
    
    // 숫자 타입도 처리 가능하도록 수정
    const hasWeight = set.weight != null && set.weight !== 0;
    const hasReps = set.reps != null && set.reps !== 0;
    const hasDuration = set.duration != null && set.duration !== 0;
    
    // 데이터에 따라 필드 설정 (숫자 값을 직접 사용)
    if (hasWeight) {
      const weightValue = parseFloat(set.weight); // 문자열일 경우를 대비해 parseFloat 사용
      data.weight = weightValue;
      // 양손 덤벨 운동이면 표시용 무게는 절반 값
      data.displayWeight = isBothArmsDumbellExercise ? weightValue / 2 : weightValue;
    }
    
    if (hasReps) {
      data.reps = parseInt(set.reps, 10); // 문자열일 경우를 대비해 parseInt 사용
    }
    
    // 시간 값 처리 - 초 단위 숫자 사용
    if (hasDuration) {
      const durationSeconds = parseInt(set.duration, 10); // 문자열일 경우를 대비해 parseInt 사용
      data.duration = durationSeconds;
      data.durationMinutes = Math.floor(durationSeconds / 60);
      data.durationSeconds = durationSeconds % 60;
    }
    
    // 운동 유형 결정 로직 (수정된 has... 변수 사용)
    let exerciseType;
    if (hasWeight) {
      exerciseType = 'weight'; // 무게 값이 있으면 웨이트 운동
    } else if (hasReps && !hasDuration) {
      exerciseType = 'bodyweight'; // 무게 없이 횟수만 있으면 맨몸 운동
    } else if (hasDuration) {
      exerciseType = 'cardio'; // 시간 값이 있으면 유산소 운동
    } else {
      // 기본값
      exerciseType = 'weight';
    }
    
    setEditingSet({
      workoutId: workout.id,
      setId: set.id,
      data,
      exerciseType,
      isBothArmsDumbellExercise,
      hasReps // 횟수 유무 추가
    });
    
    setEditingWorkout(null);
  };
  
  // 페이지 클릭 시 편집 모드 취소 이벤트 리스너 추가
  useEffect(() => {
    // 세트 편집 중인 경우에만 전역 클릭 이벤트 리스너 추가
    if (editingSet) {
      const handleClickOutside = (e) => {
        // 편집 폼이 존재하는지 확인
        const editForm = document.querySelector('.edit-set-form');
        // 클릭된 요소가 폼 내부에 없으면 편집 모드 취소
        if (editForm && !editForm.contains(e.target)) {
          setEditingSet(null);
        }
      };

      // 이벤트 리스너 등록
      document.addEventListener('mousedown', handleClickOutside);
      
      // 컴포넌트 언마운트 시 이벤트 리스너 제거
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [editingSet, setEditingSet]);
  
  // 워크아웃 이름 편집 모드에서 외부 클릭 시 취소
  useEffect(() => {
    if (editingWorkout) {
      const handleClickOutside = (e) => {
        // 편집 컨테이너가 존재하는지 확인
        const editContainer = document.querySelector('.edit-name-container');
        // 클릭된 요소가 컨테이너 내부에 없으면 편집 모드 취소
        if (editContainer && !editContainer.contains(e.target)) {
          setEditingWorkout(null);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [editingWorkout, setEditingWorkout]);
  
  // 워크아웃 이름 저장
  const handleSaveWorkoutName = async (workoutId, newName) => {
    // 이름이 빈 값인 경우 기본값 설정
    if (!newName || newName.trim() === '') {
      newName = '새 운동';
    }

    // API 요청 처리 중 상태 설정
    setIsProcessing(true);

    try {
      // 이전 이름 찾기
      const workout = workouts.find(w => w.id === workoutId);
      const oldName = workout ? workout.name : '';
      
      // 이 워크아웃 이름에 속한 모든 세트 ID 목록 찾기
      const setIdsToUpdate = [];
      workouts.forEach(w => {
        if (w.name === oldName && w.sets) {
          w.sets.forEach(set => {
            setIdsToUpdate.push(set.id);
          });
        }
      });
      
      // 벌크 업데이트 API 호출 (워크아웃은 세트의 집합이므로 세트 ID만 필요)
      await api.put('/api/v1/workouts/bulk-update-exercise', {
        workout_ids: setIdsToUpdate,
        new_exercise_name: newName
      }, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      
      // 중요: 서버에서 가장 최신 데이터를 다시 가져오도록 콜백 호출 후 즉시 반환
      if (onDataChanged) {
        onDataChanged();
        if (setEditingWorkout) {
          setEditingWorkout(null); // 편집 모드 종료
        }
        setIsProcessing(false);
        return; // 데이터 변경 알림 후 바로 종료
      }
      
      // 워크아웃 배열에서 현재 워크아웃 인덱스 찾기 (onDataChanged가 없는 경우에만 실행됨)
      const workoutIndex = workouts.findIndex(w => w.id === workoutId);
      
      if (workoutIndex !== -1) {
        // 워크아웃 객체 복사
        const updatedWorkout = { ...workouts[workoutIndex], name: newName };
        
        // 워크아웃 배열 복사 및 업데이트
        const updatedWorkouts = [...workouts];
        updatedWorkouts[workoutIndex] = updatedWorkout;
        
        // 상태 업데이트
        setWorkouts(updatedWorkouts);
      }

      // 편집 모드 종료
      if (setEditingWorkout) {
        setEditingWorkout(null);
      }
    } catch (error) {
      console.error('워크아웃 이름 저장 중 오류 발생:', error);
      // 에러 처리
    } finally {
      // 처리 상태 해제
      setIsProcessing(false);
    }
  };
  
  // 워크아웃 삭제 함수
  const handleDeleteWorkout = async (workoutId) => {
    // 사용자 확인
    if (!window.confirm('정말 이 종목과 관련된 모든 세트를 삭제하시겠습니까? 되돌릴 수 없습니다.')) {
      return;
    }
    
    try {
      // 삭제할 워크아웃 찾기
      const workout = workouts.find(w => w.id === workoutId);
      if (!workout) {
        console.error('삭제할 워크아웃을 찾을 수 없습니다:', workoutId);
        return;
      }
      
      // 삭제할 워크아웃의 이름
      const workoutName = workout.name;
      
      // 세트 ID 추출 (같은 이름을 가진 모든 워크아웃의 모든 세트)
      const setIds = [];
      
      // 모든 워크아웃에서 같은 이름을 가진 워크아웃의 세트 찾기
      workouts.forEach(w => {
        if (w.name === workoutName && w.sets) {
          w.sets.forEach(set => {
            setIds.push(set.id);
          });
        }
      });
      
      // 세트 모두 삭제 (워크아웃은 세트의 집합이므로 세트만 삭제하면 됨)
      if (setIds.length > 0) {
        await api.delete('/api/v1/workouts/bulk-delete', {
          data: { workout_ids: setIds },
          headers: {
            'Content-Type': 'application/json'
          }
        });
      }
      
      // 삭제 성공 후 상태 업데이트
      onDataChanged();
    } catch (error) {
      console.error('워크아웃 삭제 중 오류 발생:', error);
      alert('워크아웃 삭제에 실패했습니다. 다시 시도해 주세요.');
    }
  };
  
  // 세트 정보 저장 (API 사용 버전)
  const handleSaveSetInfo = async () => {
    if (!editingSet || isProcessing) return;
    
    try {
      setIsProcessing(true);
      
      // 현재 수정 중인 세트 정보
      const { setId, data } = editingSet;
      
      // 세트 ID가 문자열인 경우 숫자로 변환 (세트 ID가 실제 API 엔드포인트의 workout_id)
      const numericSetId = parseInt(setId, 10);
      
      // 세트 ID(API에서는 workout_id)로 PATCH 요청 전송
      // CORS 오류 해결을 위해 Content-Type을 명시적으로 설정
      const response = await api.patch(`/api/v1/workouts/${numericSetId}`, data, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      
      // 중요: 서버에서 가장 최신 데이터를 다시 가져오도록 콜백 호출 후 즉시 반환
      if (onDataChanged) {
        onDataChanged();
        setEditingSet(null); // 편집 모드 종료
        setIsProcessing(false);
        return; // 데이터 변경 알림 후 바로 종료
      }
      
      // 로컬 상태 업데이트 (onDataChanged가 없는 경우에만 실행됨)
      if (setWorkouts) {
        setWorkouts(prev => {
          const updatedWorkouts = prev.map(workout => {
            if (workout.id === editingSet.workoutId) {
              // 해당 워크아웃의 해당 세트 업데이트
              const updatedSets = workout.sets.map(set => {
                if (set.id === setId) {
                  // 표시 형식에 맞게 업데이트
                  const updatedSet = { ...set };
                  
                  if (data.weight !== undefined) {
                    updatedSet.weight = `${data.weight}kg`;
                  }
                  
                  if (data.reps !== undefined) {
                    updatedSet.reps = `${data.reps}회`;
                  }
                  
                  if (data.duration !== undefined) {
                    // 초 단위를 분:초 형식으로 변환
                    const minutes = Math.floor(data.duration / 60);
                    const seconds = data.duration % 60;
                    
                    if (seconds === 0) {
                      updatedSet.duration = `${minutes}분`;
                    } else {
                      updatedSet.duration = `${minutes}분 ${seconds}초`;
                    }
                  }
                  
                  return updatedSet;
                }
                return set;
              });
              
              return { ...workout, sets: updatedSets };
            }
            return workout;
          });
          
          return updatedWorkouts;
        });
      }
      
      // 편집 모드 종료
      setEditingSet(null);
    } catch (error) {
      console.error('세트 정보 변경 실패:', error);
      alert('세트 정보 변경에 실패했습니다.');
    } finally {
      setIsProcessing(false);
    }
  };
  
  // 입력 필드 값 변경 핸들러
  const handleInputChange = (fieldName, value) => {
    if (fieldName === 'durationMinutes' || fieldName === 'durationSeconds') {
      // 분/초 입력값 변경 시 데이터 객체 업데이트
      const updatedData = {...editingSet.data};
      updatedData[fieldName] = parseInt(value) || 0;
      
      // 총 초 단위 계산 (API 전송용)
      const minutes = updatedData.durationMinutes || 0;
      const seconds = updatedData.durationSeconds || 0;
      updatedData.duration = minutes * 60 + seconds;
      
      setEditingSet(prev => ({
        ...prev,
        data: updatedData
      }));
    } else if (fieldName === 'displayWeight') {
      // 양손 덤벨 운동의 경우 표시용 무게 변경 시 저장용 무게도 2배로 업데이트
      const displayValue = parseFloat(value) || 0;
      const updatedData = {...editingSet.data};
      updatedData.displayWeight = displayValue;
      
      // 양손 덤벨 운동인 경우 저장용 무게는 표시값의 2배
      if (editingSet.isBothArmsDumbellExercise) {
        updatedData.weight = displayValue * 2;
      } else {
        updatedData.weight = displayValue;
      }
      
      setEditingSet(prev => ({
        ...prev,
        data: updatedData
      }));
    } else {
      // 기존 필드 처리 방식
      setEditingSet(prev => ({
        ...prev,
        data: { ...prev.data, [fieldName]: value }
      }));
    }
  };
  
  // 세트 삭제 함수
  const handleDeleteSet = async (setId) => {
    // 사용자 확인
    if (!window.confirm('정말 이 세트를 삭제하시겠습니까? 되돌릴 수 없습니다.')) {
      return;
    }
    
    try {
      // 세트 삭제 API 호출
      await api.delete(`/api/v1/workouts/${setId}`);
      
      // 삭제 성공 후 상태 업데이트
      onDataChanged();
    } catch (error) {
      console.error('세트 삭제 중 오류 발생:', error);
      alert('세트 삭제에 실패했습니다. 다시 시도해 주세요.');
    }
  };
  
  // 세트 정보 렌더링 함수 - 값이 없거나 0인 필드는 표시하지 않음
  const formatSetInfo = (set, workout) => {
    if (!set) return '';
    
    const parts = [];
    
    // 무게 정보가 있을 경우 표시
    if (set.weight) {
      parts.push(formatWeight(set.weight, workout.name));
    }
    
    // 횟수 정보가 있을 경우 표시
    if (set.reps) {
      parts.push(formatReps(set.reps));
    }
    
    // 시간 정보가 있을 경우 표시
    if (set.duration) {
      parts.push(formatDuration(set.duration));
    }
    
    // 무게와 횟수가 모두 존재하면 ' | ' 로 구분, 아니면 공백으로 구분
    if (set.weight && set.reps) {
      return parts.join(' | ');
    } else {
      return parts.join(' ');
    }
  };
  
  // 현재 세트의 데이터 기반으로 운동 유형 판단
  const getExerciseType = (set) => {
    const hasWeight = set.weight && !set.weight.startsWith('0');
    const hasReps = set.reps && !set.reps.startsWith('0');
    const hasDuration = set.duration && !set.duration.startsWith('0');
    
    if (hasWeight) {
      return 'weight'; // 무게 있으면 웨이트 운동
    } else if (hasReps && !hasDuration) {
      return 'bodyweight'; // 무게 없이 횟수만 있으면 맨몸 운동
    } else if (hasDuration) {
      return 'cardio'; // 시간만 있으면 유산소 운동
    }
    
    // 기본값
    return 'weight';
  };

  // 세트를 생성 시간순으로 정렬하는 함수
  const sortSetsByCreatedAt = (sets) => {
    return [...sets].sort((a, b) => {
      // created_at이 없는 경우 id로 정렬 (이전 데이터 호환성)
      if (!a.created_at && !b.created_at) return a.id - b.id;
      if (!a.created_at) return 1;
      if (!b.created_at) return -1;
      
      // created_at 기준으로 시간순 정렬 (서버에서 받은 UTC 시간 기준)
      return new Date(a.created_at) - new Date(b.created_at);
    });
  };

  return (
    <WorkoutListContainer>
      {workouts.length === 0 ? (
        <NoWorkoutMessage>
          기록이 없습니다.
          <br />지금 바로 시작해 볼까요?
        </NoWorkoutMessage>
      ) : (
        <WorkoutListWrapper>
          {workouts.map((workout, index) => (
            <WorkoutItem key={workout.id}>
              <WorkoutName>
                <WorkoutNumber>종목 {index + 1}</WorkoutNumber>
                {!readOnly && editingWorkout && editingWorkout.id === workout.id ? (
                  <EditNameContainer 
                    className="edit-name-container"
                  >
                    <EditNameInput 
                      defaultValue={workout.name}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleSaveWorkoutName(workout.id, e.target.value);
                        }
                      }}
                      autoFocus
                      ref={(input) => {
                        if (input) {
                          // 모바일에서 키보드가 자동으로 나타나도록 포커스
                          setTimeout(() => input.focus(), 100);
                        }
                      }}
                    />
                    <SaveNameButton 
                      onClick={(e) => {
                        e.stopPropagation();
                        // 현재 input 값 가져오기
                        const input = e.target.closest('.edit-name-container').querySelector('input');
                        handleSaveWorkoutName(workout.id, input.value);
                      }}
                    >
                      저장
                    </SaveNameButton>
                  </EditNameContainer>
                ) : (
                  <WorkoutTitle 
                    onClick={!readOnly ? () => handleEditWorkout(workout) : undefined}
                    clickable={(!readOnly).toString()}
                  >
                    {workout.name}
                  </WorkoutTitle>
                )}
                {!readOnly && (
                  <DeleteButton onClick={(e) => {
                    e.stopPropagation(); // 이벤트 버블링 방지
                    handleDeleteWorkout(workout.id);
                  }}>
                    <FaTrash />
                  </DeleteButton>
                )}
              </WorkoutName>
              <WorkoutSets>
                {sortSetsByCreatedAt(workout.sets).map((set, setIndex) => (
                  <WorkoutSet key={set.id}>
                    <SetNumber>{setIndex + 1}세트</SetNumber>
                    {!readOnly && editingSet && editingSet.workoutId === workout.id && editingSet.setId === set.id ? (
                      <EditSetForm
                        className="edit-set-form"
                      >
                        {/* 운동 유형에 따라 다른 입력 필드 표시 */}
                        {editingSet.exerciseType === 'weight' && (
                          <>
                            <div style={{display: 'flex', alignItems: 'center'}}>
                              <EditSetInput 
                                placeholder="무게"
                                value={editingSet.data.displayWeight || ''}
                                onChange={(e) => handleInputChange('displayWeight', e.target.value)}
                                type="number"
                                min="0"
                                step="0.5"
                                autoFocus
                              />
                              <label style={{margin: '0 0.25rem', color: '#666', fontSize: '0.8rem'}}>
                                {editingSet.isBothArmsDumbellExercise ? 'kg*2' : 'kg'}
                              </label>
                            </div>
                            
                            {/* 횟수가 있는 경우에만 횟수 입력 필드 표시 */}
                            {(editingSet.hasReps || editingSet.data.reps) && (
                              <>
                                <EditSetSeparator>|</EditSetSeparator>
                                <div style={{display: 'flex', alignItems: 'center'}}>
                                  <EditSetInput 
                                    placeholder="횟수"
                                    value={editingSet.data.reps || ''}
                                    onChange={(e) => handleInputChange('reps', e.target.value)}
                                    type="number"
                                    min="0"
                                    step="1"
                                  />
                                  <label style={{margin: '0 0.25rem', color: '#666', fontSize: '0.8rem'}}>회</label>
                                </div>
                              </>
                            )}
                          </>
                        )}
                        
                        {editingSet.exerciseType === 'bodyweight' && (
                          <div style={{display: 'flex', alignItems: 'center'}}>
                          <EditSetInput 
                            placeholder="횟수"
                            value={editingSet.data.reps || ''}
                            onChange={(e) => handleInputChange('reps', e.target.value)}
                            type="number"
                            min="0"
                            step="1"
                            autoFocus
                          />
                          <label style={{margin: '0 0.25rem', color: '#666', fontSize: '0.8rem'}}>회</label>
                          </div>
                        )}
                        
                        {editingSet.exerciseType === 'cardio' && (
                          <div style={{display: 'flex', alignItems: 'center'}}>
                            <EditSetInput 
                              placeholder="분"
                              value={editingSet.data.durationMinutes || ''}
                              onChange={(e) => handleInputChange('durationMinutes', e.target.value)}
                              type="number"
                              min="0"
                              step="1"
                              autoFocus
                            />
                            <label style={{margin: '0 0.25rem', color: '#666', fontSize: '0.8rem'}}>분</label>
                            <EditSetInput 
                              placeholder="초"
                              value={editingSet.data.durationSeconds || ''}
                              onChange={(e) => handleInputChange('durationSeconds', e.target.value)}
                              type="number"
                              min="0"
                              max="59"
                              step="1"
                            />
                            <label style={{margin: '0 0.25rem', color: '#666', fontSize: '0.8rem'}}>초</label>
                          </div>
                        )}
                        
                        <SaveSetButton 
                          onClick={handleSaveSetInfo}
                        >저장</SaveSetButton>
                      </EditSetForm>
                    ) : (
                      <>
                        <SetInfo 
                          onClick={(e) => {
                            e.stopPropagation();
                            if (readOnly) return;
                            
                            if (typeof handleEditSet === 'function') {
                              handleEditSet(workout, set);
                            } else {
                              console.error('handleEditSet is not a function:', handleEditSet);
                            }
                          }}
                          clickable={(!readOnly).toString()}
                        >
                          {formatSetInfo(set, workout)}
                        </SetInfo>
                        {!readOnly && (
                          <DeleteSetButton onClick={(e) => {
                            e.stopPropagation(); // 이벤트 버블링 방지
                            handleDeleteSet(set.id);
                          }}>
                            <FaTimes />
                          </DeleteSetButton>
                        )}
                      </>
                    )}
                  </WorkoutSet>
                ))}
              </WorkoutSets>
            </WorkoutItem>
          ))}
        </WorkoutListWrapper>
      )}
    </WorkoutListContainer>
  );
};

// 스타일 컴포넌트
const WorkoutListContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const WorkoutListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const WorkoutItem = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: .5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const WorkoutName = styled.div`
  background-color: ${({ theme }) => theme.colors.primary};
  padding: 0.5rem 1rem;
  color: white;
  font-weight: 500;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const WorkoutNumber = styled.span`
  display: inline-block;
  background-color: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.gray700};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  padding: 0.25rem 0.25rem;
  border-radius: 4px;
  margin-right: 0.5rem;
  min-width: 3rem;
  text-align: center;
`;

const WorkoutTitle = styled.div.attrs(props => ({
  style: {
    cursor: props.clickable ? 'pointer' : 'default'
  }
}))`
  font-size: ${({ theme }) => theme.fontSizes.md};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  color: ${({ theme }) => theme.colors.white};
  flex: 1;
  margin-right: 0.5rem;
  
  &:hover {
    color: ${({ theme, clickable }) => clickable ? theme.colors.gray200 : theme.colors.white};
  }
`;

const DeleteButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  cursor: pointer;
  
  &:hover {
    color: ${({ theme }) => theme.colors.gray100};
  }
`;

const WorkoutSets = styled.div`
  display: flex;
  flex-direction: column;
  padding-left: .5rem;
`;

const WorkoutSet = styled.div`
  display: flex;
  align-items: center;
  padding: 0.5rem 1rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray200};
  background-color: white;
  
  &:last-child {
    border-bottom: none;
  }
`;

const SetNumber = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.xs};
  color: ${({ theme }) => theme.colors.gray700};
  width: 48px;
`;

const SetInfo = styled.span.attrs(props => ({
  style: {
    cursor: props.clickable ? 'pointer' : 'default'
  }
}))`
  flex: 1;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.gray900};
  
  &:hover {
    color: ${({ theme, clickable }) => clickable ? theme.colors.primary : theme.colors.gray900};
  }
`;

const DeleteSetButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.gray500};
  cursor: pointer;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const EditNameContainer = styled.div`
  display: flex;
  align-items: center;
`;

const EditNameInput = styled.input`
  flex: 1;
  padding: 0;
  border: none;
  background-color: transparent;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  color: white;
  min-width: 0;
  width: 100%;
  
  &:focus {
    outline: none;
    font-weight: ${({ theme }) => theme.fontWeights.semibold};
    border-bottom: 1px solid;
  }
  
  /* number 타입 인풋의 스핀(증감) 버튼 제거 */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  
  /* Firefox에서 스핀 버튼 제거 */
  &[type=number] {
    -moz-appearance: textfield;
  }
`;

const SaveNameButton = styled.button`
  padding: 0;
  padding-right: 1rem;
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  border-radius: 4px;
  font-size: ${({ theme }) => theme.fontSizes.xs};
  cursor: pointer;
  margin-left: 0.5rem;
  font-weight: bold;
  // min-width: px;
`;

const EditSetForm = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
`;

const EditSetInput = styled.input`
  width: 40px;
  padding: 0.25rem;
  border: 1px solid ${({ theme }) => theme.colors.gray300};
  border-radius: 4px;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
  
  /* number 타입 인풋의 스핀(증감) 버튼 제거 */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  
  /* Firefox에서 스핀 버튼 제거 */
  &[type=number] {
    -moz-appearance: textfield;
  }
`;

const EditSetSeparator = styled.span`
  margin: 0 0.5rem;
  color: ${({ theme }) => theme.colors.gray500};
`;

const SaveSetButton = styled.button`
  padding: 0.25rem 0.5rem;
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  border-radius: 4px;
  font-size: ${({ theme }) => theme.fontSizes.xs};
  cursor: pointer;
  margin-left: 0.5rem;
  font-weight: bold;
  // min-width: 60px;
`;

const NoWorkoutMessage = styled.p`
  text-align: center;
  color: ${({ theme }) => theme.colors.gray600};
  line-height: 1.5;
  margin-top: 2rem;
`;

export default WorkoutList; 